// Utilitários de moeda e conversão

const LOCALE_BY_CURRENCY = {
  USD: 'en-US',
  BRL: 'pt-BR',
  EUR: 'de-DE',
};

const SYMBOL_BY_CURRENCY = {
  USD: '$',
  BRL: 'R$',
  EUR: '€',
};

/** Formata valor para exibição com símbolo e separadores por moeda (ex.: $1,234.56 / R$ 1.234,56) */
export const formatCurrencyDisplay = (value, currency = 'USD') => {
  const num = Number(value);
  if (Number.isNaN(num)) return currency === 'BRL' ? 'R$ 0,00' : '$0.00';
  const locale = LOCALE_BY_CURRENCY[currency] || 'en-US';
  const symbol = SYMBOL_BY_CURRENCY[currency] || '$';
  const formatted = num.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return symbol === '$' ? `${symbol}${formatted}` : `${symbol} ${formatted}`;
};

/** Formata número para uso em campo de valor (sem símbolo; com separador de milhar e decimal por moeda) */
export const formatAmountForInput = (value, currency = 'USD') => {
  const num = Number(value);
  if (Number.isNaN(num)) return '';
  const locale = LOCALE_BY_CURRENCY[currency] || 'en-US';
  return num.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

/** Formata para exibição no campo ao sair (blur): sempre 2 casas decimais (USD = . / BRL = ,) */
export const formatAmountForBlur = (value, currency = 'USD') => {
  const num = Number(value);
  if (Number.isNaN(num) || num < 0) return '';
  const locale = LOCALE_BY_CURRENCY[currency] || 'en-US';
  return num.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Sanitiza o que o usuário digita: apenas dígitos e o separador decimal da moeda.
 * USD ($) = ponto (.) | BRL (R$) = vírgula (,). Limita a 2 casas decimais.
 * A formatação completa (milhares, etc.) é aplicada no blur do campo.
 */
export const sanitizeAmountInput = (str, currency = 'USD') => {
  if (str == null) return '';
  const isBRL = currency === 'BRL';
  const decimalSep = isBRL ? ',' : '.';
  const re = isBRL ? /[^\d,]/g : /[^\d.]/g;
  const joined = String(str).replace(re, '');
  if (joined === '') return '';
  const parts = joined.split(decimalSep);
  if (parts.length === 1) return parts[0];
  const intPart = parts[0];
  const decPart = parts.slice(1).join('').slice(0, 2);
  return decPart === '' ? intPart + decimalSep : intPart + decimalSep + decPart;
};

/**
 * Converte string do input (com máscara) em número.
 * USD: vírgula = milhar, ponto = decimal. BRL: ponto = milhar, vírgula = decimal.
 */
export const parseAmountFromInput = (str, currency = 'USD') => {
  if (str == null || String(str).trim() === '') return NaN;
  const s = String(str).trim();
  if (currency === 'BRL') {
    const withoutThousands = s.replace(/\./g, '');
    const parts = withoutThousands.split(',');
    if (parts.length === 1) return parseFloat(parts[0]) || NaN;
    const withDecimalPoint = parts[0] + '.' + parts.slice(1).join('');
    return parseFloat(withDecimalPoint) || NaN;
  }
  const withoutThousands = s.replace(/,/g, '');
  return parseFloat(withoutThousands) || NaN;
};

export const formatCurrency = (value, currency = 'USD') => {
  const symbol = SYMBOL_BY_CURRENCY[currency] || '$';
  return formatCurrencyDisplay(value, currency);
};

export const convertCurrency = (amount, fromCurrency, toCurrency) => {
  const rates = {
    USD: 1,
    BRL: 5.15,
    EUR: 0.92,
  };

  const amountInUSD = amount / rates[fromCurrency];
  return amountInUSD * rates[toCurrency];
};

export const calculateFee = (amount, feePercentage = 2.5) => {
  return (amount * feePercentage) / 100;
};

export const calculateTotal = (amount, feePercentage = 2.5) => {
  const fee = calculateFee(amount, feePercentage);
  return amount + fee;
};
